import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import date from 'date-and-time';
import { faArrowTrendUp } from '@fortawesome/pro-light-svg-icons/faArrowTrendUp';
import { TypedMemo } from '../../../common/types';
import { useArtistsTopGaining } from '../../hooks/useArtistsTopGainingHook';
import { useSMLDatePickerDateParam } from '../../hooks/useSMLDatePickerDateParam';
import { useSMLPaginationParam } from '../../hooks/useSMLPaginationParam';
import { useSMLCountryParam } from '../../hooks/useSMLCountryParam';
import { useSMLGenreParam } from '../../hooks/useSMLGenreParam';
import { SMLResultsCard } from '../SMLResultsCard/SMLResultsCard';
import { SMLResultsItem } from '../SMLResultsCard/props';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { datePickerParamName } from '../SMLDatePicker/consts';
import { countryParamName } from '../SMLCountrySelector/consts';
import { genreParamName } from '../SMLGenreSelector/consts';
import { ValueChangeElement } from '../PositionChangeElement';

const paginationParamName = 'gainingPage';

const TopGainingArtistsCardComponent: React.FC = () => {
  const intl = useIntl();
  const { selectedDate, selectedDateValue } = useSMLDatePickerDateParam();
  const { currentPage } = useSMLPaginationParam(paginationParamName);
  const { countryCode } = useSMLCountryParam();
  const { genre } = useSMLGenreParam();
  const {
    data: artistsData,
    isLoading,
    error,
    refetch,
  } = useArtistsTopGaining({
    page: currentPage,
    date: selectedDate,
  });

  const artists = useMemo(() => artistsData?.artists || [], [artistsData?.artists]);

  const items: SMLResultsItem[] = useMemo(
    () =>
      artists.map((artist) => ({
        id: artist.artistMaid,
        name: artist.mainArtistName,
        position: artist.songAddedPosition.current,
        imageUrl: artist.imageUrl,
        rightElement: <ValueChangeElement currentValue={artist.addedToPlaylists.current} />,
        chart: { entityType: 'artist' as const, date: selectedDate },
        route: SMLRoutes.artistDetails,
        params: {
          artistId: artist.artistMaid,
          [datePickerParamName]: selectedDateValue,
          ...(countryCode !== undefined ? { [countryParamName]: countryCode } : {}),
          ...(genre !== undefined ? { [genreParamName]: genre } : {}),
        },
      })),
    [artists, selectedDate, selectedDateValue, countryCode, genre]
  );

  const title = intl.formatMessage({
    id: 'app.sml.home.topGaining.title',
    defaultMessage: 'Top Gaining Artists',
  });

  const subtitle = date.format(selectedDate, 'MMM DD, YYYY');

  return (
    <SMLResultsCard
      title={title}
      subtitle={subtitle}
      icon={faArrowTrendUp}
      iconColor="var(--pos)"
      items={items}
      isLoading={isLoading}
      error={error}
      onRetry={() => {
        refetch().catch(console.error);
      }}
      showCountryFilter
      showGenreFilter
      paginationParamName={paginationParamName}
    />
  );
};

export const TopGainingArtistsCard = TypedMemo(TopGainingArtistsCardComponent);
